/**
 * Customer Portal Pinia Store
 *
 * This store manages the state and operations for the customer portal,
 * including filing data, beneficial owners, and various completion checks.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getApiConfiguration } from '../../api'
import { BoiReportsApi, BeneficialOwnerFromJSON, CompanyApplicantFromJSON } from '../../api-client'
import { useRouter } from 'vue-router'
import { useUserStore } from './userStore'

// Initialize the BOI Reports API client
const boiClient = new BoiReportsApi(getApiConfiguration(SERVER_URL_BASE))

export const useFilingDetailStore = defineStore('filingDetail', () => {
    const router = useRouter()

    // Use the user store to get the current user's information
    const userStore = useUserStore()

    // State
    const currentFiling = ref({})
    const currentTeam = ref({})
    const uploadProgress = ref(0)
    const uploadResult = ref(null)

    // Getters (computed properties)
    const beneficialOwners = computed(() => currentFiling.value.beneficialOwners || [])
    const companyApplicants = computed(() => currentFiling.value.companyApplicants || [])

    const isFilingEditable = computed(() => {
        const editableStatuses = ['created', 'progress', 'reviewed', 'revision'];
        return editableStatuses.includes(currentFiling.value.filingStatus);
    });
    /**
     * Computed property for overall percentage complete
     * @returns {number} Percentage of completion (0-100)
     */
    const percentComplete = computed(() => {
        // Add a comment explaining the change
        // Check if the filing is for an existing reporting company
        const isExistingReportingCompany = currentFiling.value.existingReportingCompany || false;

        let totalSections = 6;
        let completedSections = [
            isBasicInfoComplete,
            isFormationComplete,
            isTaxDetailsComplete,
            isAddressComplete,
            isTribalInfoComplete,
            allOwnersComplete
        ].filter(check => check.value).length;

        // Include company applicants check only if not an existing reporting company
        if (!isExistingReportingCompany) {
            totalSections++;
            if (allCompanyApplicantsComplete.value) {
                completedSections++;
            }
        }

        return Math.round((completedSections / totalSections) * 100);
    });


    // Completion check computed properties
    const isFormationComplete = computed(
        () =>
            !!currentFiling.value.stateOfFormation &&
            !!currentFiling.value.countryOfFormation
    )

    const isTaxDetailsComplete = computed(() => {
        const { taxIdType, taxIdNumber } = currentFiling.value
        return (
            !!taxIdType &&
            !!taxIdNumber &&
            (taxIdType !== 'foreign' ? [10, 11].includes(taxIdNumber.length) : true)
        )
    })

    const isTribalInfoComplete = computed(
        () =>
            !currentFiling.value.tribalJurisdiction ||
            (!!currentFiling.value.tribalJurisdiction &&
                !!currentFiling.value.nameOfTribe)
    )

    const isBasicInfoComplete = computed(
        () =>
            !!currentFiling.value.reportingCompanyLegalName &&
            !!currentFiling.value.contactEmail
    )

    const isAddressComplete = computed(() => {
        return (
            !!currentFiling.value.businessAddress1 &&
            !!currentFiling.value.businessCity &&
            !!currentFiling.value.businessState &&
            !!currentFiling.value.businessZip &&
            !!currentFiling.value.businessCountry
        )
    })

    const isCompanyDetailsComplete = computed(
        () =>
            isBasicInfoComplete.value &&
            isFormationComplete.value &&
            isTaxDetailsComplete.value &&
            isTribalInfoComplete.value
    )

    const isReadyForSubmission = computed(() =>
        percentComplete.value === 100
    )

    /**
    * Checks if a company applicant's information is complete
    * @param {Object} applicant - The company applicant object
    * @returns {boolean} True if the applicant's information is complete, false otherwise
    */
    const isApplicantComplete = (applicant) => {
        // If the applicant has a FinCEN ID, consider it complete
        if (applicant.fincenIdentifier) {
            return true
        }

        // Otherwise, check all required fields
        const requiredFields = [
            'firstName',
            'lastName',
            'dateOfBirth',
            'idImage',
            'addressLine1',
            'addressCity',
            'addressState',
            'addressZip',
            'addressCountry'
        ];

        return requiredFields.every(field => {
            if (field === 'dateOfBirth' || field === 'idImage') {
                return !!applicant[field]
            }
            return typeof applicant[field] === 'string' && applicant[field].trim() !== '';
        })
    }

    const allCompanyApplicantsComplete = computed(() =>
        companyApplicants.value.length > 0 && companyApplicants.value.every(isApplicantComplete)
    )

    /**
     * Checks if a beneficial owner's information is complete
     * @param {Object} owner - The beneficial owner object
     * @returns {boolean} True if the owner's information is complete, false otherwise
     */
    const isOwnerComplete = (owner) => {
        const requiredFields = [
            'firstName',
            'lastName',
            'dateOfBirth',
            'idImage',
            'addressLine1',
            'addressCity',
            'addressState',
            'addressZip'
        ]
        return requiredFields.every((field) => {
            if (field === 'dateOfBirth' || field === 'idImage') {
                return !!owner[field]
            }
            return typeof owner[field] === 'string' && owner[field].trim() !== ''
        })
    }

    const allOwnersComplete = computed(
        () =>
            beneficialOwners.value.length > 0 &&
            beneficialOwners.value.every(isOwnerComplete)
    )

    /**
     * Computes a list of incomplete items in the filing
     * @returns {string[]} Array of messages describing incomplete items
     */
    const incompleteItems = computed(() => {
        const items = []

        if (!isBasicInfoComplete.value) {
            if (!currentFiling.value.reportingCompanyLegalName) {
                items.push('Company Legal Name is required')
            }
        }

        if (!isFormationComplete.value) {
            if (!currentFiling.value.stateOfFormation) {
                items.push('State of Formation is required')
            }
            if (!currentFiling.value.countryOfFormation) {
                items.push('Country of Formation is required')
            }
        }

        if (!isTaxDetailsComplete.value) {
            if (!currentFiling.value.taxIdType) {
                items.push('Tax ID Type is required')
            }
            if (!currentFiling.value.taxIdNumber) {
                items.push('Tax ID Number is required')
            } else if (
                currentFiling.value.taxIdType !== 'foreign' &&
                currentFiling.value.taxIdNumber.length !== 10 &&
                currentFiling.value.taxIdNumber.length !== 11
            ) {
                items.push(
                    'Tax ID Number must be 10 or 11 digits for non-foreign entities'
                )
            }
        }

        if (!isTribalInfoComplete.value && currentFiling.value.tribalJurisdiction) {
            if (!currentFiling.value.nameOfTribe) {
                items.push('Name of Tribe is required for Tribal Jurisdiction')
            }
        }

        if (!isAddressComplete.value) {
            if (!currentFiling.value.businessAddress1) {
                items.push('Business Address is required')
            }
            if (!currentFiling.value.businessCity) {
                items.push('Business City is required')
            }
            if (!currentFiling.value.businessState) {
                items.push('Business State is required')
            }
            if (!currentFiling.value.businessZip) {
                items.push('Business ZIP Code is required')
            }
            if (!currentFiling.value.businessCountry) {
                items.push('Business Country is required')
            }
        }

        if (!allOwnersComplete.value) {
            items.push("All Beneficial Owners must be complete");
        }

        // Add check for company applicants if not an existing reporting company
        if (!currentFiling.value.existingReportingCompany) {
            if (companyApplicants.value.length === 0) {
                items.push("At least one Company Applicant is required if your business was formed after January 1, 2024.");
            } else if (!allCompanyApplicantsComplete.value) {
                items.push("All Company Applicants must be complete");
            }
        }

        return items
    })

    // Actions

    /**
     * Navigates to the FilingsDetail page for the current filing
     */
    function goToFilingDetail() {
        if (currentFiling.value && currentTeam.value) {
            router.push({
                name: 'FilingsDetail',
                params: {
                    teamSlug: currentTeam.value.slug,
                    filingId: currentFiling.value.id
                }
            })
        } else {
            console.error("Filing or team information is missing")
        }
    }

    /**
     * Fetches BOI report data from the API
     * @param {string} filingId - The ID of the filing to fetch
     * @param {string} teamSlug - The slug of the team
     */
    async function getBoiReportData(filingId, teamSlug) {
        try {
            const response = await boiClient.boiReportsRetrieve({
                id: filingId,
                teamSlug
            })
            currentFiling.value = response
            currentTeam.value = { slug: teamSlug, id: response.team }
        } catch (error) {
            console.error('Error fetching BOI report data:', error)
            throw error
        }
    }

    /**
     * Updates the current filing data
     * @param {Object} filing - The filing data to set
     */
    function setCurrentFiling(filing) {
        currentFiling.value = filing
    }

    /**
     * Updates the current team data
     * @param {Object} team - The team data to set
     */
    function setCurrentTeam(team) {
        currentTeam.value = team
    }

    /**
     * Updates the filing with new data
     * @param {Object} updateData - The data to update in the filing
     * @returns {Promise} The updated filing data
     */
    async function updateFiling(updateData) {
        try {
            const dataWithPercent = {
                ...updateData,
                percentComplete: percentComplete.value
            }
            const response = await boiClient.boiReportsPartialUpdate({
                id: currentFiling.value.id,
                teamSlug: currentTeam.value.slug,
                patchedBeneficialOwnershipReport: dataWithPercent
            })
            currentFiling.value = { ...currentFiling.value, ...dataWithPercent }
            console.log(
                'Filing updated with new percent complete:',
                percentComplete.value
            )
            return response
        } catch (error) {
            console.error('Error updating filing:', error)
            throw error
        }
    }

    // Wrapper functions for specific updates
    const updateBusinessAddress = (addressData) => updateFiling(addressData)
    const updateCompanyInfo = (companyData) => updateFiling(companyData)

    /**
     * Submits the BOI report for processing
     * @returns {Promise} The updated filing data
     */
    async function submitBoiForProcessing() {
        try {
            if (!userStore.user) {
                throw new Error('User information is missing')
            }
            // Extract necessary user data
            const { firstName, lastName, email } = userStore.user || {}

            // Validate required fields
            if (!firstName || !lastName || !email) {
                throw new Error('Required user information is missing')
            }

            // Prepare the payload with user data
            const payload = {
                filingStatus: 'submitted_for_processing',
                submittedByFirstName: firstName,
                submittedByLastName: lastName,
                submittedByEmail: email
            }

            // Make the API call with the updated payload
            const response = await boiClient.boiReportsPartialUpdate({
                id: currentFiling.value.id,
                teamSlug: currentTeam.value.slug,
                patchedBeneficialOwnershipReport: payload
            })

            setCurrentFiling(response)

            return response

        } catch (error) {
            console.error('Error submitting for processing:', error)
            throw error
        }
    }

    /**
   * Reopens the BOI Report for editing
   * @returns {Promise} The updated filing data
   */
    async function changeFilingStatus(filingStatus) {
        try {
            const response = await boiClient.boiReportsPartialUpdate({
                id: currentFiling.value.id,
                teamSlug: currentTeam.value.slug,
                patchedBeneficialOwnershipReport: {
                    filingStatus: filingStatus
                }
            })
            setCurrentFiling(response)

            return response

        } catch (error) {
            console.error('Error submitting for processing:', error)
            throw error
        }
    }

    /**
     * Checks if the filing is editable
     * @returns {boolean} True if the filing is editable, false otherwise
     */
    function checkFilingEditable() {
        return isFilingEditable.value
    }


    /**
     * Retrieves a company applicant by ID
     * @param {string} id - The ID of the company applicant
     * @returns {Object|undefined} The company applicant object if found, undefined otherwise
     */
    function getCompanyApplicantById(id) {
        return companyApplicants.value.find(applicant => applicant.id === id)
    }

    /**
    * Adds a new company applicant
    * @param {Object} values - The company applicant data
    * @returns {Promise} The created company applicant data
    */
    async function addCompanyApplicant(values) {
        try {
            const formattedValues = {
                ...values,
                // Only include dateOfBirth if it's present and valid
                ...(values.dateOfBirth && {
                    dateOfBirth: values.dateOfBirth instanceof Date
                        ? values.dateOfBirth
                        : new Date(values.dateOfBirth)
                })
            }

            const response = await boiClient.companyApplicantsCreate({
                teamSlug: currentTeam.value.slug,
                companyApplicant: formattedValues
            })

            if (!currentFiling.value.companyApplicants) {
                currentFiling.value.companyApplicants = []
            }
            currentFiling.value.companyApplicants.push(response)
            await updateFiling({})
            return response
        } catch (error) {
            console.error('Error adding company applicant:', error)
            throw error
        }
    }

    /**
     * Removes a company applicant from the filing
     * @param {string} id - The ID of the company applicant to remove
     */
    async function removeCompanyApplicantFromFiling(id) {
        try {
            await boiClient.companyApplicantsDestroy({
                teamSlug: currentTeam.value.slug,
                id: id,
            })
            currentFiling.value.companyApplicants = currentFiling.value.companyApplicants.filter(applicant => applicant.id !== id)
            await updateFiling({})
        } catch (error) {
            console.error('Error removing company applicant:', error)
            throw error
        }
    }

    /**
     * Updates a company applicant's data
     * @param {string} id - The ID of the company applicant to update
     * @param {Object} applicantData - The updated applicant data
     * @returns {Promise} The updated company applicant data
     */
    async function updateCompanyApplicant(id, applicantData) {
        try {
            const formattedValues = {
                ...applicantData,
                dateOfBirth: applicantData.dateOfBirth instanceof Date
                    ? applicantData.dateOfBirth
                    : new Date(applicantData.dateOfBirth)
            }
            const response = await boiClient.companyApplicantsPartialUpdate({
                id: id,
                teamSlug: currentTeam.value.slug,
                patchedCompanyApplicant: formattedValues
            })
            const index = currentFiling.value.companyApplicants.findIndex(applicant => applicant.id === id)
            if (index !== -1) {
                currentFiling.value.companyApplicants[index] = response
            }
            await updateFiling({})
            return response
        } catch (error) {
            console.error('Error updating company applicant:', error)
            throw error
        }
    }


    /**
     * Uploads a company applicant's ID image
     * @param {File} fileData - The file to upload
     * @param {string} idType - The type of ID being uploaded
     * @returns {Promise} The upload response
     */
    async function uploadCompanyApplicant(fileData, idType) {
        try {
            // Note: You may need to create a new API endpoint for uploading company applicant IDs
            // For now, we'll use the same endpoint as beneficial owners
            return await boiClient.uploadIdImage({
                teamSlug: currentTeam.value.slug,
                filingId: currentFiling.value.id,
                beneficialOwnershipReport: currentFiling.value.id,
                idImage: fileData,
                idType: idType,
                uploadType: 'company_applicant',
            })
        } catch (error) {
            console.error('Error uploading company applicant:', error)
            throw error
        }
    }

    /**
     * Retrieves a beneficial owner by ID
     * @param {string} id - The ID of the beneficial owner
     * @returns {Object|undefined} The beneficial owner object if found, undefined otherwise
     */
    function getBeneficialOwnerById(id) {
        return beneficialOwners.value.find(owner => owner.id === id)
    }


    /**
     * Adds a new beneficial owner
     * @param {Object} values - The beneficial owner data
     * @returns {Promise} The created beneficial owner data
     */
    async function addBeneficialOwner(values) {
        try {
            const formattedValues = {
                ...values,
                dateOfBirth:
                    values.dateOfBirth instanceof Date
                        ? values.dateOfBirth
                        : new Date(values.dateOfBirth)
            }
            const response = await boiClient.beneficialOwnersCreate({
                teamSlug: currentTeam.value.slug,
                beneficialOwner: formattedValues
            })
            if (!currentFiling.value.beneficialOwners) {
                currentFiling.value.beneficialOwners = []
            }
            currentFiling.value.beneficialOwners.push(response)
            await updateFiling({})
            return response
        } catch (error) {
            console.error('Error adding beneficial owner:', error)
            throw error
        }
    }

    /**
     * Removes a beneficial owner from the filing
     * @param {string} id - The ID of the beneficial owner to remove
     */
    async function removeBeneficialOwnerFromFiling(id) {
        try {
            await boiClient.beneficialOwnersDestroy({
                teamSlug: currentTeam.value.slug,
                id: id
            })
            currentFiling.value.beneficialOwners =
                currentFiling.value.beneficialOwners.filter((owner) => owner.id !== id)
            await updateFiling({})
        } catch (error) {
            console.error('Error removing beneficial owner:', error)
            throw error
        }
    }

    /**
     * Updates a beneficial owner's data
     * @param {string} id - The ID of the beneficial owner to update
     * @param {Object} ownerData - The updated owner data
     * @returns {Promise} The updated beneficial owner data
     */
    async function updateBeneficialOwner(id, ownerData) {
        try {
            const formattedValues = {
                ...ownerData,
                dateOfBirth:
                    ownerData.dateOfBirth instanceof Date
                        ? ownerData.dateOfBirth
                        : new Date(ownerData.dateOfBirth)
            }
            const response = await boiClient.beneficialOwnersPartialUpdate({
                id: id,
                teamSlug: currentTeam.value.slug,
                patchedBeneficialOwner: formattedValues
            })
            const index = currentFiling.value.beneficialOwners.findIndex(
                (owner) => owner.id === id
            )
            if (index !== -1) {
                currentFiling.value.beneficialOwners[index] = response
            }
            await updateFiling({})
            return response
        } catch (error) {
            console.error('Error updating beneficial owner:', error)
            throw error
        }
    }

    /**
     * Uploads a beneficial owner's ID image
     * @param {File} fileData - The file to upload
     * @param {string} idType - The type of ID being uploaded
     * @returns {Promise} The upload response
     */
    async function uploadBeneficialOwner(fileData, idType) {
        try {
            return await boiClient.uploadIdImage({
                teamSlug: currentTeam.value.slug,
                filingId: currentFiling.value.id,
                beneficialOwnershipReport: currentFiling.value.id,
                idImage: fileData,
                idType: idType,
                uploadType: 'beneficial_owner',
            })
        } catch (error) {
            console.error('Error uploading beneficial owner:', error)
            throw error
        }
    }

    /**
     * Checks the status of a task
     * @param {string} taskId - The ID of the task to check
     * @returns {Promise} The task status
     */
    async function checkTaskStatus(taskId) {
        try {
            return await boiClient.taskStatus({
                teamSlug: currentTeam.value.slug,
                taskId: taskId
            })
        } catch (error) {
            console.error('Error checking task status:', error)
            throw error
        }
    }

    /**
     * Updates the progress of a file upload task
     * @param {string} taskId - The ID of the task to update
     * @param {string} entityType - The entity type (either Company Applicant or Beneficial Owner)
     * @returns {Promise} The updated beneficial owner or company applicant data if successful
     */
    async function updateProgress(taskId, entityType) {
        let completed = false;

        while (!completed) {
            try {
                const response = await checkTaskStatus(taskId);

                switch (response.state) {
                    case 'SUCCESS':
                        return handleSuccess(response, entityType);

                    case 'FAILURE':
                        handleFailure(response);
                        completed = true;
                        break;

                    default:
                        updateProgressUI();
                        await new Promise((resolve) => setTimeout(resolve, 500));
                        break;
                }
            } catch (err) {
                console.error('Error updating progress:', err);
                throw err;
            }
        }
    }

    /**
     * Handles success response and updates the relevant data
     * @param {Object} response - The response from the task status check
     * @param {string} entityType - The entity type
     * @returns {Object} - The created entity (Beneficial Owner or Company Applicant)
     */
    function handleSuccess(response, entityType) {
        uploadProgress.value = 100;
        uploadResult.value = response.result;

        const entity = createEntity(response.result.extracted_data, entityType);
        addEntityToFiling(entityType, entity);

        return entity;
    }

    /**
     * Handles failure response and updates the UI
     * @param {Object} response - The failure response from the task status check
     */
    function handleFailure(response) {
        uploadProgress.value = 100;
        uploadResult.value = response.result;
        throw new Error(response.result);
    }

    /**
     * Updates the UI progress when the task is still in progress
     */
    function updateProgressUI() {
        uploadProgress.value = Math.min(uploadProgress.value + 5, 90);
    }

    /**
     * Creates an entity (Beneficial Owner or Company Applicant) from the extracted data
     * @param {Object} extractedData - The extracted data from the response
     * @param {string} entityType - The entity type
     * @returns {Object} - The created entity
     */
    function createEntity(extractedData, entityType) {
        if (entityType === 'beneficialOwner') {
            return BeneficialOwnerFromJSON(extractedData);
        } else if (entityType === 'companyApplicant') {
            return CompanyApplicantFromJSON(extractedData);
        }
    }

    /**
     * Adds the created entity to the current filing
     * @param {string} entityType - The entity type
     * @param {Object} entity - The created entity
     */
    function addEntityToFiling(entityType, entity) {
        if (entityType === 'beneficialOwner') {
            if (!currentFiling.value.beneficialOwners) {
                currentFiling.value.beneficialOwners = [];
            }
            currentFiling.value.beneficialOwners.push(entity);
        } else if (entityType === 'companyApplicant') {
            if (!currentFiling.value.companyApplicants) {
                currentFiling.value.companyApplicants = [];
            }
            currentFiling.value.companyApplicants.push(entity);
        }
    }

    /**
     * Clears the upload progress and result
     */
    function clearUploadProgress() {
        uploadProgress.value = 0
        uploadResult.value = null
    }

    return {
        // State
        currentFiling,
        currentTeam,
        uploadProgress,
        uploadResult,

        // Getters
        beneficialOwners,
        companyApplicants,
        isFilingEditable,
        isReadyForSubmission,
        isFormationComplete,
        isTaxDetailsComplete,
        isTribalInfoComplete,
        isBasicInfoComplete,
        isCompanyDetailsComplete,
        allOwnersComplete,
        allCompanyApplicantsComplete,
        isAddressComplete,
        percentComplete,
        incompleteItems,

        // Actions
        goToFilingDetail,
        getBoiReportData,
        setCurrentFiling,
        setCurrentTeam,
        updateBusinessAddress,
        updateCompanyInfo,
        getBeneficialOwnerById,
        getCompanyApplicantById,
        updateBeneficialOwner,
        updateCompanyApplicant,
        removeBeneficialOwnerFromFiling,
        removeCompanyApplicantFromFiling,
        addBeneficialOwner,
        addCompanyApplicant,
        submitBoiForProcessing,
        changeFilingStatus,
        checkFilingEditable,
        uploadBeneficialOwner,
        uploadCompanyApplicant,
        checkTaskStatus,
        updateProgress,
        clearUploadProgress,
    }
})
